import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Image,
  Trash2,
  Plus,
  Download,
} from 'lucide-react'

const gridLayouts = [
  { id: '2x2', name: '2×2', cols: 2, rows: 2 },
  { id: '3x3', name: '3×3', cols: 3, rows: 3 },
  { id: '4x4', name: '4×4', cols: 4, rows: 4 },
  { id: '2x3', name: '2×3', cols: 2, rows: 3 },
  { id: '3x2', name: '3×2', cols: 3, rows: 2 },
]

interface GridImage {
  id: string
  url: string
  alt: string
}

export default function ImageGrid() {
  const [selectedLayout, setSelectedLayout] = useState('3x3')
  const [images, setImages] = useState<GridImage[]>([])
  const [gap, setGap] = useState(4)
  const [borderRadius, setBorderRadius] = useState(8)

  const currentLayout = gridLayouts.find(l => l.id === selectedLayout) || gridLayouts[1]
  const totalSlots = currentLayout.cols * currentLayout.rows

  const handleAddImage = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      const newImages = files.map((file, i) => ({
        id: `img-${Date.now()}-${i}`,
        url: URL.createObjectURL(file),
        alt: file.name,
      }))
      setImages(prev => [...prev, ...newImages].slice(0, totalSlots))
    }
    input.click()
  }

  const handleRemoveImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id))
  }

  const handleClearAll = () => {
    setImages([])
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className="p-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">图片九宫格</h1>
          <p className="mt-2 text-muted-foreground">
            将多张图片拼接成九宫格，适合社交媒体发布
          </p>
        </div>
        <div className="flex gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClearAll}
            className="inline-flex items-center gap-2 rounded-xl border border-border/50 px-4 py-2.5 text-sm font-medium hover:bg-secondary/30 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            清空
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-medium text-background shadow-lg shadow-foreground/20"
          >
            <Download className="h-4 w-4" />
            导出
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left: Settings */}
        <div className="lg:col-span-1 space-y-6">
          {/* Layout Selection */}
          <div className="rounded-xl border border-border/50 bg-card/50 p-6">
            <h2 className="font-semibold mb-4">布局</h2>
            <div className="grid grid-cols-3 gap-2">
              {gridLayouts.map((layout) => (
                <motion.button
                  key={layout.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedLayout(layout.id)}
                  className={`rounded-lg p-3 text-sm font-medium transition-all ${
                    selectedLayout === layout.id
                      ? 'bg-foreground text-background'
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                >
                  {layout.name}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Gap Setting */}
          <div className="rounded-xl border border-border/50 bg-card/50 p-6">
            <h2 className="font-semibold mb-4">间距</h2>
            <input
              type="range"
              min="0"
              max="20"
              value={gap}
              onChange={(e) => setGap(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>0px</span>
              <span>{gap}px</span>
              <span>20px</span>
            </div>
          </div>

          {/* Border Radius */}
          <div className="rounded-xl border border-border/50 bg-card/50 p-6">
            <h2 className="font-semibold mb-4">圆角</h2>
            <input
              type="range"
              min="0"
              max="50"
              value={borderRadius}
              onChange={(e) => setBorderRadius(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>0px</span>
              <span>{borderRadius}px</span>
              <span>50px</span>
            </div>
          </div>

          {/* Add Image Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAddImage}
            className="w-full inline-flex items-center justify-center rounded-xl border-2 border-dashed border-border/50 px-6 py-4 text-sm font-medium hover:border-foreground/30 hover:bg-secondary/30 transition-all"
          >
            <Plus className="mr-2 h-4 w-4" />
            添加图片 ({images.length}/{totalSlots})
          </motion.button>
        </div>

        {/* Right: Preview */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border/50 bg-card/50 p-6">
            <h2 className="font-semibold mb-4">预览</h2>
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${currentLayout.cols}, 1fr)`,
                gridTemplateRows: `repeat(${currentLayout.rows}, 1fr)`,
                gap: `${gap}px`,
              }}
            >
              {Array.from({ length: totalSlots }).map((_, index) => {
                const image = images[index]
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="aspect-square relative group"
                    style={{ borderRadius: `${borderRadius}px` }}
                  >
                    {image ? (
                      <>
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                          style={{ borderRadius: `${borderRadius}px` }}
                        />
                        <div
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          style={{ borderRadius: `${borderRadius}px` }}
                        >
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRemoveImage(image.id)}
                            className="rounded-full bg-white/20 p-2 hover:bg-white/30 transition-colors"
                          >
                            <Trash2 className="h-4 w-4 text-white" />
                          </motion.button>
                        </div>
                      </>
                    ) : (
                      <div
                        className="w-full h-full bg-secondary/50 flex items-center justify-center cursor-pointer hover:bg-secondary/70 transition-colors"
                        style={{ borderRadius: `${borderRadius}px` }}
                        onClick={handleAddImage}
                      >
                        <Image className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
